/**
 * logger.ts — Centralized logger for the web frontend using the "loglevel" library.
 *
 * Why loglevel:
 * - Lightweight, zero-dependency logging with leveled methods (trace, debug, info, warn, error).
 * - Works in browsers and test environments without extra setup.
 * - Can be configured at runtime (via env/localStorage) without code changes.
 *
 * Usage:
 *   import { logger } from "./logger.js";
 *   logger.info("UI ready");
 *   logger.debug("Fetching", { endpoint, payload });
 *   logger.error("Request failed", err);
 *
 * Configuration precedence (highest first):
 * - window.LOG_LEVEL or globalThis.LOG_LEVEL (when present)
 * - localStorage["LOG_LEVEL"] (browser only)
 * - import.meta.env.VITE_LOG_LEVEL (Vite env)
 * - import.meta.env.MODE === "development" ? "debug" : "info" (default)
 *
 * Supported levels (loglevel): "trace" | "debug" | "info" | "warn" | "error" | "silent"
 */

import log from "loglevel";

/**
 * Derive the desired log level from environment sources.
 * Falls back to a sensible default based on the build mode.
 */
export function resolveLevel(envOverride?: { VITE_LOG_LEVEL?: string; MODE?: string }): log.LogLevelDesc {
  // 1) Explicit global override (useful in tests or quick debugging in console)
  const glb = (globalThis as unknown as { LOG_LEVEL?: string }).LOG_LEVEL;
  if (typeof glb === "string" && glb.trim()) {
    return glb.toLowerCase() as log.LogLevelDesc;
  }

  // 2) Browser localStorage (allows ad-hoc tweaking without rebuild)
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const ls = window.localStorage.getItem("LOG_LEVEL");
      if (typeof ls === "string" && ls.trim()) {
        return ls.toLowerCase() as log.LogLevelDesc;
      }
    }
  } catch {
    // Ignore access errors (e.g., disabled storage) and continue
  }

  // 3) Vite env variable (typed access that works in Vitest/Node too)
  const env = envOverride ?? ((import.meta as unknown as { env?: { VITE_LOG_LEVEL?: string; MODE?: string } }).env ?? {});
  const viteLevel = env.VITE_LOG_LEVEL;
  if (typeof viteLevel === "string" && viteLevel.trim()) {
    return viteLevel.toLowerCase() as log.LogLevelDesc;
  }

  // 4) Default by mode
  const mode = env.MODE;
  if (mode === "development" || mode === "test") {
    return "debug";
  }
  return "info";
}

// Create a named logger for easier filtering/identification if multiple loggers are used later.
export const logger = log.getLogger("web");
logger.setLevel(resolveLevel());

// Optional: expose a tiny helper for consistent action correlation in UI flows.
/**
 * Create a short correlation ID suitable for tying together related log lines
 * during a single user interaction.
 */
export function newCid(prefix = "web"): string {
  // Not cryptographically strong; sufficient for correlating logs in a session.
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
